import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Evidence, EvidenceAbnt, EvidenceAbntType, Indicator, IndicatorStatus, SaveIndicator} from 'app/shared/objects/certification';
import {FileItem, FileUploader} from 'ng2-file-upload';
import {Observable} from 'rxjs';
import {CityProfileService} from 'app/restricted/city-profile/service/city-profile.service';
import {Compress} from 'app/shared/util/compress.utils';
import {environment} from 'environments/environment';
import {AuthService} from 'app/auth/auth.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {DialogShowEvidenceComponent} from '../dialog-show-evidence/dialog-show-evidence.component';
import {DialogAddLinkComponent} from '../dialog-add-link/dialog-add-link.component';
import { DialogAttachEvidenceComponent } from '../dialog-attach-evidence/dialog-attach-evidence.component';

interface IndicatorCard extends Indicator {
	isOpen: boolean;
}

@Component({
	selector: 'app-certification-iso-abnt-area-form',
	templateUrl: './certification-iso-abnt-area-form.component.html',
	styleUrls: ['./certification-iso-abnt-area-form.component.scss']
})
export class CertificationIsoAbntAreaFormComponent implements OnInit {
	@ViewChild('cropInput') cropInput: ElementRef<HTMLInputElement>;
	@ViewChild('fileSelect', {static: false}) fileSelect: ElementRef;

	form: UntypedFormGroup;

	isMain: boolean;
	isOpen: boolean;
	hasData: boolean;
	hasNumeratorAndDenominator: boolean;

	public uploader: FileUploader;
	uploader$: Observable<any>;
	uploadMessage = '';
	uploadError = false;
	hasFile = false;

	loadingFile = false;
	progressFile = 0;

	initialData: SaveIndicator;

	@Input() indicator: IndicatorCard;
	@Input() certification: string;
	@Input() area: string;
	@Input() year: number;
	@Output() submitIndicator = new EventEmitter<SaveIndicator>();
	@Output() submitEvidence = new EventEmitter<{file?: File, link?: string, description: string, type: EvidenceAbntType}>();
	@Output() removeEvidence = new EventEmitter<number>();

	showPdf = false;
	srcPdf = '';

	constructor(
		private cityProfileService: CityProfileService,
		private readonly fb: UntypedFormBuilder,
		private zone: NgZone,
		private authService: AuthService,
		private readonly dialog: MatDialog,
	) {
	}

	get evidences(): UntypedFormArray {
		return this.form.get('evidences') as UntypedFormArray;
	}

	get links(): UntypedFormArray {
		return this.form.get('links') as UntypedFormArray;
	}

	ngOnInit(): void {
		this.form = this.fb.group({
			moreInformation: [null, [Validators.required]],
			denominator: [null, [Validators.required]],
			numerator: [null, [Validators.required]],
			value: [null, [Validators.required]],
			indicatorId: [this.indicator.id],
			abntId: [null],
		});

		this.isMain = this.indicator.certificationType === 'ESSENTIAL';
		this.isOpen = this.indicator.isOpen;
		this.hasData = !!this.indicator.indicatorInfo;

		if (this.indicator.indicatorInfo) {
			this.fillForm(this.indicator.indicatorInfo);
		}

		this.setupHasNumeratorAndDenominator(this.indicator.hasNumeratorAndDenominator);
	}

	fillForm(indicator: SaveIndicator): void {
		this.initialData = indicator;
		this.form.patchValue(indicator);

		if (indicator.status !== IndicatorStatus.IN_FILLING) {
			this.isOpen = false;
		}

		if (!this.isOpen) {
			this.form.disable()
		}
	}

	toggleOpen(): void {
		this.isOpen = !this.isOpen;

		if (this.isOpen) {
			this.startFiles();
		}
	}

	toggleEnableForm(): void {
		if (this.form.enabled) {
			this.form.disable();
			return
		}

		this.form.enable();
	}

	public async downloadModel(): Promise<void> {
		await this.cityProfileService.getPreSignedModelDownload(this.certification, this.indicator.id)
			.then((blob: string) => {
				const link = document.createElement('a');
				link.href = blob;
				link.click();
			})
	}

	clearForm(): void {
		this.form.reset();

		if (this.initialData) {
			this.fillForm(this.initialData);
		}
	}

	submit(): void {
		if (this.form.valid) {
			this.toggleOpen();

			let indicator = this.form.getRawValue();

			indicator = {
				...indicator,
				year: this.year,
			}

			this.submitIndicator.emit(indicator);
		}
	}

	onCompleteUploadFileItem = (fileItem: FileItem, response: any, status) => {
		this.uploadMessage = '';
		if (response.success) {
			this.evidences.push(this.fb.control(response.data.url));
		} else {
			this.uploadError = true;
			this.uploadMessage = 'Erro ao enviar imagem';
		}
		fileItem.remove();
	};

	fileOver(e) {
		this.hasFile = e;
	}

	startFiles() {
		const url = environment.apiURL + `/cityCertification/${this.certification}/area/${this.area}/evidence`;

		this.uploader = new FileUploader({
			url: url,
			autoUpload: true,
			isHTML5: true
		});

		this.uploader.onBeforeUploadItem = (item) => {
			item.withCredentials = false;
			item.headers = [{
				name: 'Authorization',
				value: 'Bearer ' + this.authService.accessToken
			}];
		}

		this.uploader.onProgressAll = (e) => {
			this.progressFile = e;
			this.loadingFile = true;
		};

		this.uploader.onCompleteAll = () => {
			this.progressFile = 0;
			this.loadingFile = false;
		};

		this.uploader.onCompleteItem = (item: FileItem, response: any, status: any) => {
			this.evidences.push(this.fb.control({
				fileUrl: response,
				originalFileName: item.file.name,
				type: item.file.type
			}));

			item.remove();
		};
	}

	fileType(type: string, search: string): boolean {
		if (type) return type.includes(search);
	}

	onChange() {
		const files: FileList = this.fileSelect.nativeElement.files;
		this.zone.runOutsideAngular(() => {
			const promises: Promise<Blob>[] = [];
			for (let i = 0; i < files.length; ++i) {
				const file = files[i];
				promises.push(Compress.compressImage(file));
			}

			Promise.all(promises).then(_files => this.zone.run(() => this.uploadImage(_files)));
		});
	}

	async uploadImage(files) {
		for (const blob of files) {
			const file = new File([blob], blob['name']);

			if (file.size <= 4194304) {
				this.uploadMessage = `Enviando imagem ${blob.name}`;
				this.uploadError = false;

				this.cityProfileService.uploadEvidence(file, this.certification, this.area)
					.subscribe(
						response => {
							if (response) {
								this.evidences.push(this.fb.control(response));
								this.uploadMessage = null;
							} else {
								this.uploadError = true;
								this.uploadMessage = 'Erro ao enviar imagem';
							}
						},
						err => {
							console.error({err})
							this.uploadError = true;
							this.uploadMessage = 'Erro ao enviar imagem';
						}
					)
			} else {
				this.uploadMessage = `Erro ao enviar imagem (Peso máximo 4 MB)`;
				this.uploadError = true;
			}
		}
	}

	drop(event: CdkDragDrop<any>) {
		this.evidences.controls = this.move(
			this.evidences.controls,
			event.previousContainer.data.index,
			event.container.data.index
		);
	}

	move(arr, old_index, new_index) {
		while (old_index < 0) {
			old_index += arr.length;
		}
		while (new_index < 0) {
			new_index += arr.length;
		}
		if (new_index >= arr.length) {
			var k = new_index - arr.length;
			while (k-- + 1) {
				arr.push(undefined);
			}
		}
		arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
		return arr;
	}

	removeUpload(index: number): void {
		this.evidences.removeAt(index);
	}

	showEvidence(evidence): void {

		this.cityProfileService.getPrivateEvidenceUrl(evidence.value, this.certification, this.area)
			.subscribe(
				res => {
					if (res) {
						const dialogConfig = {
							maxWidth: '90%',
							width: '1200px',
							maxHeight: '90%',
							height: '900px',
							hasBackdrop: true,
							closeOnNavigation: true
						}
						const data = {url: res, type: evidence.value.type};
						this.dialog.open(DialogShowEvidenceComponent, {...dialogConfig, data})
					}
				}
			)


	}

	addLink(): void {
		const dialogConfig = {
			maxWidth: '90%',
			width: '600px',
			hasBackdrop: true,
			closeOnNavigation: true
		}

		this.dialog.open(DialogAddLinkComponent, {...dialogConfig})
			.afterClosed()
			.subscribe(
				res => {
					this.links.push(this.fb.control(res.link))
				}
			);
	}

	removeLink(index: number): void {
		this.links.removeAt(index);
	}

	goToLink(url: string) {
		window.open(url, "_blank");
	}

	private setupHasNumeratorAndDenominator(hasNumeratorAndDenominator: boolean): void {
		this.hasNumeratorAndDenominator = hasNumeratorAndDenominator;

		if (!this.hasNumeratorAndDenominator) {
			this.form.get('numerator').clearValidators();
			this.form.get('denominator').clearValidators();
		}
	}

	public documentEvidenceList(): EvidenceAbnt[] {
		return this.indicator?.indicatorInfo?.evidencesABNT?.filter(evidence => evidence.evidenceType === EvidenceAbntType.DOCUMENT);
	}

	public linkEvidenceList(): EvidenceAbnt[] {
		return this.indicator?.indicatorInfo?.evidencesABNT?.filter(evidence => evidence.evidenceType === EvidenceAbntType.LINK);
	}

	public openReviewFile(evidence: EvidenceAbnt): void {
		if (evidence.url) {
			this.cityProfileService.getPrivateEvidenceUrl(evidence, this.certification, this.area)
			.subscribe({
				next: (res) => {
					this.openDialogAttachEvidence(evidence.evidenceType, {...evidence, fileUrl: res});
				},
				error: (err) => {
					this.openDialogAttachEvidence(evidence.evidenceType, evidence);
				}
			})
		} else {
			this.openDialogAttachEvidence(evidence.evidenceType, evidence);
		}
	}

	public openReviewLink(evidence: EvidenceAbnt): void {
		this.openDialogAttachEvidence(evidence.evidenceType, evidence);
	}

	public openDialogAttachEvidence(evidenceType: EvidenceAbntType, evidence?: {fileUrl?: string, link?: string, description: string}): void {

		const dialogConfig: MatDialogConfig = {
			maxHeight: '100dvh',
			maxWidth: '100vw',
			hasBackdrop: true,
			closeOnNavigation: true,
			data: {
				evidenceType,
				preview: evidence,
			}
		};

		this.dialog.open(DialogAttachEvidenceComponent, {...dialogConfig})
			.afterClosed()
			.subscribe(res => {
				if (res) {
					this.submitEvidence.emit({...res, type: evidenceType});
				}
			});

	}

	public handleRemoveEvidence(evidenceId: number): void {
		this.removeEvidence.emit(evidenceId);
	}

	public getColorStatus(status: IndicatorStatus): string {
		switch (status) {
			case IndicatorStatus.IN_FILLING:
				return '#D4D4D4';
			case IndicatorStatus.FILLED:
				return '#257DE1';
			case IndicatorStatus.ABSTENTION:
				return '#FFB619';
			case IndicatorStatus.COMPLIANT:
				return '#00CB94';
			case IndicatorStatus.NON_CONFORMING:
				return '#FFB619';
			case IndicatorStatus.WAITING:
				return '#D4D4D4';
		}
	}
}
